import { type LoaderFunctionArgs, redirect } from "react-router";
import type { MinimalAppResponse } from "src/queries/apps/apps.types";
import type { Task } from "src/queries/tasks/tasks.types";
import {
  TASK_RUN_API_STATUSES,
  type TaskRunApiStatus,
} from "src/utils/appRoutes/appRoutes.schemas";
import type {
  ClusterRunPath,
  PitPath,
  TaskRunPath,
} from "src/utils/appRoutes/appRoutes.types";
import { buildLink } from "src/utils/buildLink/buildLink";
import { fetchOutsideReact } from "src/utils/fetchOutsideReact";

const isTaskRunApiStatus = (value: string | null): value is TaskRunApiStatus =>
  value != null && (TASK_RUN_API_STATUSES as readonly string[]).includes(value);

/**
 * Redirects task-run routes which have "latest" as "app_pit" (since it was unknown in the context the link was created)
 */
export const taskRedirectRouterLoader = async ({
  params: { task_id, env_name, app_name, task_name, app_pit, cluster_name },
  request,
}: LoaderFunctionArgs): Promise<Response> => {
  const redirectionUrl = new URL(request.url);

  // request.url never includes the hash (client-only); always read from window.location
  const searchParams = new URLSearchParams(
    redirectionUrl.search || window.location.search
  );
  const rawStatus = searchParams.get("status");
  const status = isTaskRunApiStatus(rawStatus) ? rawStatus : undefined;
  searchParams.delete("status");
  const search = searchParams.toString();

  const urlParams = {
    hash: window.location.hash,
    search: search ? `?${search}` : "",
  };

  const task = task_id
    ? await fetchOutsideReact<Task | null>({
        url: `/api/tasks/${task_id}`,
        body: { with_enrichments: false },
      })
    : (((await fetchOutsideReact<Task[]>({
        url: `/api/envs/${env_name}/apps/${app_name}/task-runs`,
        body: {
          task_name: task_name ?? cluster_name,
          ...(app_pit !== "latest" ? { app_pit } : {}),
          ...(status ? { status } : {}),
          limit: 1,
        },
      })) ?? [])[0] ?? null);

  if (task) {
    const pathParams = cluster_name
      ? ({
          ...urlParams,
          app_name: task.app_name,
          task_id: task.task_id,
          cluster_name,
          run_time: task.app_pit,
        } as ClusterRunPath)
      : ({
          ...urlParams,
          app_name: task.app_name,
          task_id: task.task_id,
          app_pit: task.app_pit,
        } as TaskRunPath);

    const url = buildLink(task.env_name, pathParams);
    if (!url)
      throw new Response("Failed to build task redirect URL", { status: 500 });
    return redirect(url);
  }

  // Fallback: redirect to app-level route if task doesn't exist
  const app = await fetchOutsideReact<MinimalAppResponse>({
    url: `/api/envs/${env_name}/apps/${app_name}/info`,
  });

  const url = buildLink(app.env_name, {
    ...urlParams,
    app_name: app.app_name,
    app_pit,
  } as PitPath);
  if (!url) throw new Response("Failed to build app redirect URL", { status: 500 });
  return redirect(url);
};
